package diana.soleil.design.iraniancanadienbazzar.feature.home

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppAvatar
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.AppNotification
import diana.soleil.design.iraniancanadienbazzar.core.model.NotificationType
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_campaign
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_channel
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_comment
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_event
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_gem
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_message
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_order
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_order_cancelled
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_order_confirmed
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_order_fulfilled
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_other
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_review_reply
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_time_days_ago
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_time_hours_ago
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_time_just_now
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_time_minutes_ago
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_time_weeks_ago
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notification_unread_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_error
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_mark_all_read
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_retry
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_signin_action
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_signin_desc
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_signin_title
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.notifications_title
import kotlin.time.Clock
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Activity notifications (opened from the Home bell): chat messages, review replies, post comments,
 * order updates, and new-event announcements the server created under `users/{uid}/notifications`.
 * Rows render the server-stamped display snapshot (sender/business name, avatar, content preview,
 * relative time); docs written before the enrichment shipped fall back to a generic per-type label.
 * Tapping a row marks it read and routes to the source (message → conversation, review reply →
 * business, comment → post, order → orders, event → event tickets). Guests see a sign-in
 * placeholder with a sign-in action (auto-bouncing here used to race the two navigations and
 * left a blank white screen on web); load failures get an error state with retry.
 */
@Composable
fun NotificationsRoute(
    onBack: () -> Unit,
    onOpenConversation: (String) -> Unit,
    onOpenBusiness: (String) -> Unit,
    onOpenPost: (String) -> Unit,
    onOpenOrders: () -> Unit,
    onOpenEvent: (String) -> Unit,
    onOpenChannel: (businessId: String) -> Unit = {},
    onOpenGem: (kind: String, gemId: String) -> Unit = { _, _ -> },
    /** Campaign rows carry a full universal-link URL — the host routes it via the App-Links parser. */
    onOpenLink: (String) -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: NotificationsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    NotificationsScreen(
        state = state,
        onBack = onBack,
        onMarkAllRead = viewModel::markAllRead,
        onSignIn = onRequireSignIn,
        onRetry = viewModel::retry,
        onClick = { notification ->
            viewModel.markRead(notification)
            // Route by the id the server stamped for this type (see firebase/functions/index.js).
            when {
                notification.type == NotificationType.Gem ->
                    onOpenGem(notification.gemKind.orEmpty(), notification.gemId.orEmpty())
                notification.link != null -> onOpenLink(notification.link!!)
                notification.conversationId != null -> onOpenConversation(notification.conversationId!!)
                notification.postId != null -> onOpenPost(notification.postId!!)
                notification.orderId != null -> onOpenOrders()
                notification.eventId != null -> onOpenEvent(notification.eventId!!)
                notification.type == diana.soleil.design.iraniancanadienbazzar.core.model.NotificationType.Channel &&
                    notification.businessId != null -> onOpenChannel(notification.businessId!!)
                notification.businessId != null -> onOpenBusiness(notification.businessId!!)
            }
        },
        modifier = modifier,
    )
}

@Composable
private fun NotificationsScreen(
    state: NotificationsUiState,
    onBack: () -> Unit,
    onMarkAllRead: () -> Unit,
    onSignIn: () -> Unit,
    onRetry: () -> Unit,
    onClick: (AppNotification) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxSize().background(AppTheme.colors.background),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.surface)
                .statusBarsPadding().navigationBarsPadding()
                .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xs),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.notifications_back_a11y),
                onClick = onBack,
            )
            Text(
                text = stringResource(Res.string.notifications_title),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onSurface,
                modifier = Modifier.weight(1f),
            )
            if (state.hasUnread) {
                AppIconButton(
                    iconName = "done_all",
                    contentDescription = stringResource(Res.string.notifications_mark_all_read),
                    onClick = onMarkAllRead,
                )
            }
        }

        when {
            state.loading -> LoadingState()
            // Guests get a placeholder with an explicit sign-in action instead of a bounce.
            state.signInRequired -> EmptyState(
                title = stringResource(Res.string.notifications_signin_title),
                description = stringResource(Res.string.notifications_signin_desc),
                actionLabel = stringResource(Res.string.notifications_signin_action),
                onAction = onSignIn,
            )
            state.error -> ErrorState(
                message = stringResource(Res.string.notifications_error),
                onRetry = onRetry,
                retryLabel = stringResource(Res.string.notifications_retry),
            )
            state.notifications.isEmpty() -> EmptyState(
                title = stringResource(Res.string.notifications_empty_title),
                description = stringResource(Res.string.notifications_empty_desc),
            )
            else -> LazyColumn(
                // Reading-width cap keeps rows comfortable on wide/desktop web.
                modifier = Modifier.widthIn(max = AppTheme.sizes.readingMaxWidth).fillMaxWidth(),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                items(state.notifications, key = { it.id }) { notification ->
                    NotificationRow(notification, onClick = { onClick(notification) })
                }
            }
        }
    }
}

@Composable
private fun NotificationRow(notification: AppNotification, onClick: () -> Unit) {
    AppCard(modifier = Modifier.fillMaxWidth().clickable(onClick = onClick)) {
        Row(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (notification.title != null || notification.photoUrl != null) {
                AppAvatar(
                    name = notification.title,
                    photoUrl = notification.photoUrl,
                    size = AppTheme.sizes.avatarMd,
                    fallbackIconName = typeIconName(notification.type),
                )
            } else {
                // Legacy docs without a display snapshot keep the original type-icon circle.
                Box(
                    modifier = Modifier
                        .size(AppTheme.sizes.iconCircle)
                        .background(AppTheme.colors.primaryContainer, AppTheme.shapes.pill),
                    contentAlignment = Alignment.Center,
                ) {
                    AppIcon(
                        name = typeIconName(notification.type),
                        size = AppTheme.sizes.iconMd,
                        tint = AppTheme.colors.primary,
                    )
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
            ) {
                Text(
                    text = notification.title ?: stringResource(typeLabelRes(notification.type)),
                    style = AppTheme.typography.labelMd,
                    color = AppTheme.colors.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                val preview = previewText(notification)
                if (preview != null) {
                    Text(
                        text = preview,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
                val createdAt = notification.createdAtMillis
                if (createdAt != null) {
                    Text(
                        text = relativeTimeLabel(createdAt),
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.outline,
                    )
                }
            }
            if (!notification.read) {
                val unreadLabel = stringResource(Res.string.notification_unread_a11y)
                Box(
                    modifier = Modifier
                        .size(AppTheme.sizes.chartLegendDot)
                        .background(AppTheme.colors.primary, AppTheme.shapes.pill)
                        .semantics { contentDescription = unreadLabel },
                )
            }
        }
    }
}

private fun typeIconName(type: NotificationType): String = when (type) {
    NotificationType.Message -> "chat_bubble"
    NotificationType.ReviewReply -> "rate_review"
    NotificationType.Comment -> "mode_comment"
    NotificationType.Order -> "receipt_long"
    NotificationType.Event -> "event"
    NotificationType.Campaign -> "campaign"
    NotificationType.Channel -> "campaign"
    NotificationType.Gem -> "auto_awesome"
    NotificationType.Other -> "notifications"
}

private fun typeLabelRes(type: NotificationType) = when (type) {
    NotificationType.Message -> Res.string.notification_message
    NotificationType.ReviewReply -> Res.string.notification_review_reply
    NotificationType.Comment -> Res.string.notification_comment
    NotificationType.Order -> Res.string.notification_order
    NotificationType.Event -> Res.string.notification_event
    NotificationType.Campaign -> Res.string.notification_campaign
    NotificationType.Channel -> Res.string.notification_channel
    NotificationType.Gem -> Res.string.notification_gem
    NotificationType.Other -> Res.string.notification_other
}

/**
 * Second line of a row: the server-stamped content snippet, except orders, whose status is carried
 * as a raw code and localized here (works for legacy docs too — `orderStatus` predates `preview`).
 */
@Composable
private fun previewText(notification: AppNotification): String? =
    if (notification.type == NotificationType.Order) {
        when (notification.orderStatus) {
            "confirmed" -> stringResource(Res.string.notification_order_confirmed)
            "fulfilled" -> stringResource(Res.string.notification_order_fulfilled)
            "cancelled" -> stringResource(Res.string.notification_order_cancelled)
            else -> notification.preview
        }
    } else {
        notification.preview
    }

/**
 * "2 h ago"-style label bucketed to the largest sensible unit (localized digits via
 * [formatCount]). Clock skew (future timestamps) clamps to "just now".
 */
@Composable
internal fun relativeTimeLabel(millis: Long): String {
    val minutes = ((Clock.System.now().toEpochMilliseconds() - millis) / 60_000L).coerceAtLeast(0)
    val lang = AppTheme.language
    return when {
        minutes < 1 -> stringResource(Res.string.notification_time_just_now)
        minutes < 60 -> stringResource(Res.string.notification_time_minutes_ago, lang.formatCount(minutes))
        minutes < 60 * 24 -> stringResource(Res.string.notification_time_hours_ago, lang.formatCount(minutes / 60))
        minutes < 60 * 24 * 7 -> stringResource(Res.string.notification_time_days_ago, lang.formatCount(minutes / (60 * 24)))
        else -> stringResource(Res.string.notification_time_weeks_ago, lang.formatCount(minutes / (60 * 24 * 7)))
    }
}
